import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NavigateFunction } from 'react-router-dom';

import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch } from '@/shared';
import { CategoriaServicios, CategoriasServiciosPagingRes } from '@/shared/interfaces';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchCategoriaServicios = ({
  page = 1,
  page_size = 10,
  nombre = '',
}: GetCategoriaServiciosParams) => {
  return useQuery({
    queryKey: ['categoria-servicio', page, nombre, page_size],
    queryFn: async () => {
      return getCategoriaServicios({ page, nombre, page_size });
    },
  });
};

export const useGetCategoriaServicio = (id: string) => {
  return useQuery({
    queryKey: ['categoria-servicio', id],
    queryFn: async () => {
      return getCategoriaServicio(id);
    },
  });
};

export const useCreateCategoriaServicios = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCategoriaServicios,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-servicio'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

export const useUpdateCategoriaServicios = (navigate: NavigateFunction, returnUrl: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateCategoriaServicios,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoria-servicio'] });
    },
    onSettled: () => {
      navigate(returnUrl);
    },
  });
};

///* axios
export type GetCategoriaServiciosParams = {
  page?: number;
  page_size?: number;
  nombre?: string;
};

export type CreateCategoriaServiciosParams = Omit<CategoriaServicios, 'id_plazo_pago'>;
export type UpdateCategoriaServiciosParams = {
  id: string;
  data: CreateCategoriaServiciosParams;
};

export const getCategoriaServicios = async (params?: GetCategoriaServiciosParams) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<CategoriasServiciosPagingRes>(
    `${urlBackend}api/v1/categorias-servicio?${queryParams}`,
    true
  );
};

export const getCategoriaServicio = async (id: string) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await get<CategoriaServicios>(`${urlBackend}api/v1/categorias-servicio/${id}/`, true);

  res.status === 'error' && setAlert(true, res.message, res.status);
  return res;
};

export const createCategoriaServicios = async (data: CreateCategoriaServiciosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<CategoriaServicios>(`${urlBackend}api/v1/categorias-servicio/`, data, true);

  setAlert(true, res.message, res.status);
  return res;
};

export const updateCategoriaServicios = async ({ id, data }: UpdateCategoriaServiciosParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await put<CategoriaServicios>(`${urlBackend}api/v1/categorias-servicio/${id}/`, data, true);
  setAlert(true, res.message, res.status);
  return res;
};
